import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '../components/dev-tools-card.js';

import { shopBrandStyles } from '../styles/shop-brand-styles';
import { activeCurrency } from '../datatypes/currency';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { WithRealtimeConsumer } from '../mixins/with-realtime-consumer';
import { ApiFactory } from '../api/easypos-api';
import { IActiveOrderSummary, ISalesReport } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';

const Base = WithRealtimeConsumer(
  WithActiveShopConsumer(WithUserProfileConsumer(LitElement))
);

interface QuickAction {
  label: string;
  description: string;
  icon: string;
  route: string;
  tone?: 'navy' | 'gold' | 'leaf' | 'plain';
  ownerOnly?: boolean;
}

@customElement('app-home')
export class AppHome extends Base {
  // Today's live snapshot for the at-a-glance KPI strip.
  @state() private _activeOrders: IActiveOrderSummary[] = [];
  @state() private _salesReport: ISalesReport | null = null;
  private _refreshTimer?: number;

  static styles = [
    shopBrandStyles,
    css`
      :host {
        display: block;
      }

      .grid {
        display: grid;
        gap: 1rem;
      }

      /* ===== Hero strip (compact welcome) ===== */
      .hero {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 0.4rem;
        flex-wrap: wrap;
      }
      .hero-left {
        flex: 1;
        min-width: 0;
      }
      .hero-eyebrow {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--s-ink-faint);
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 0.5rem;
      }
      .hero-eyebrow .live-dot {
        width: 7px;
        height: 7px;
      }
      .hero h1 {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(1.8rem, 5vw, 2.4rem);
        line-height: 1.05;
        letter-spacing: -0.03em;
        margin: 0 0 0.2rem;
        color: var(--s-ink);
      }
      .hero h1 .name-accent {
        color: var(--s-gold);
      }
      .hero-shop {
        font-size: 0.95rem;
        color: var(--s-ink-soft);
        font-weight: 500;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
      .role-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 9px;
        background: var(--s-navy);
        color: white;
        border-radius: 999px;
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      /* ===== Today snapshot (KPI strip) ===== */
      .snapshot {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.75rem;
        margin-bottom: 0.4rem;
      }
      @media (max-width: 600px) {
        .snapshot {
          grid-template-columns: 1fr 1fr;
        }
        .snapshot .kpi-tile:nth-child(3) {
          grid-column: span 2;
        }
      }
      .kpi-tile {
        padding: 0.95rem 1.1rem;
      }
      .kpi-tile .kpi-value {
        font-size: 1.65rem;
      }
      .kpi-tile .icon-bubble {
        position: absolute;
        right: 12px;
        top: 12px;
        width: 28px;
        height: 28px;
        border-radius: 9px;
        background: var(--s-canvas-soft);
        color: var(--s-ink-soft);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
      }
      .kpi-tile.accent-gold .icon-bubble {
        background: rgba(255, 255, 255, 0.12);
        color: var(--s-gold-bright);
      }

      /* ===== Quick actions ===== */
      .actions-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        margin: 0.4rem 0.2rem 0.5rem;
      }
      .actions-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
      }
      .actions-grid .primary {
        grid-column: span 2;
      }
      @media (min-width: 780px) {
        .actions-grid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
        .actions-grid .primary {
          grid-column: span 3;
        }
      }

      .action {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.85rem;
        padding: 1.15rem 1.2rem 1.1rem;
        background: var(--s-canvas);
        border: 1px solid var(--s-line);
        border-radius: 14px;
        cursor: pointer;
        text-align: left;
        text-decoration: none;
        color: inherit;
        font: inherit;
        box-shadow: var(--s-shadow-soft);
        transition:
          transform 160ms cubic-bezier(0.16, 0.84, 0.44, 1),
          box-shadow 160ms ease,
          border-color 160ms ease;
        overflow: hidden;
      }
      .action:hover {
        transform: translateY(-2px);
        box-shadow: var(--s-shadow-lift);
        border-color: var(--s-ink-faint);
      }
      .action-icon {
        width: 40px;
        height: 40px;
        border-radius: 11px;
        background: var(--s-canvas-soft);
        color: var(--s-ink);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
      }
      .action.gold .action-icon {
        background: var(--s-gold);
        color: var(--s-ink);
      }
      .action.leaf .action-icon {
        background: var(--s-good-tint);
        color: var(--s-good);
      }
      .action-label {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 1.05rem;
        letter-spacing: -0.015em;
        line-height: 1.1;
      }
      .action-desc {
        font-size: 0.85rem;
        color: var(--s-ink-soft);
        line-height: 1.45;
        font-weight: 500;
      }
      .action-arrow {
        position: absolute;
        right: 1rem;
        top: 1.05rem;
        color: var(--s-ink-faint);
        font-size: 14px;
        transition:
          transform 160ms ease,
          color 160ms ease;
      }
      .action:hover .action-arrow {
        color: var(--s-ink);
        transform: translateX(3px);
      }

      /* Hero primary action — make the "New sale" stand out as the main CTA */
      .action.primary {
        background: linear-gradient(135deg, var(--s-navy), var(--s-navy-soft));
        color: white;
        border-color: transparent;
        flex-direction: row;
        align-items: center;
        gap: 1.1rem;
        padding: 1.4rem 1.5rem;
      }
      .action.primary .action-icon {
        background: var(--s-gold);
        color: var(--s-navy);
        width: 50px;
        height: 50px;
        font-size: 22px;
        border-radius: 14px;
        flex: none;
      }
      .action.primary .action-text {
        flex: 1;
        min-width: 0;
      }
      .action.primary .action-label {
        font-size: 1.3rem;
        color: white;
      }
      .action.primary .action-desc {
        color: rgba(255, 255, 255, 0.8);
      }
      .action.primary .action-arrow {
        position: static;
        color: rgba(255, 255, 255, 0.7);
        font-size: 18px;
        flex: none;
      }
      .action.primary:hover .action-arrow {
        color: var(--s-gold);
      }

      /* ===== Empty state (no shop) ===== */
      .no-shop {
        padding: 2rem 1.75rem;
        background: linear-gradient(
          135deg,
          var(--s-canvas),
          var(--s-canvas-soft)
        );
        border: 1px solid var(--s-line);
        border-radius: 18px;
      }
      .no-shop h2 {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 1.6rem;
        line-height: 1.1;
        letter-spacing: -0.025em;
        margin: 0.5rem 0 0.4rem;
        color: var(--s-ink);
      }
      .no-shop p {
        color: var(--s-ink-soft);
        font-size: 0.95rem;
        line-height: 1.5;
        margin: 0 0 1.2rem;
        max-width: 50ch;
      }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    void this._refresh();
    // Light polling — KPI strip stays fresh-ish without thrashing the API.
    this._refreshTimer = window.setInterval(() => void this._refresh(), 30_000);
    document.addEventListener('visibilitychange', this._onVisibility);
  }
  disconnectedCallback(): void {
    if (this._refreshTimer) window.clearInterval(this._refreshTimer);
    document.removeEventListener('visibilitychange', this._onVisibility);
    super.disconnectedCallback();
  }
  private _onVisibility = () => {
    if (document.visibilityState === 'visible') void this._refresh();
  };

  updated(changed: Map<string, unknown>) {
    super.updated?.(changed);
    if (changed.has('activeShop')) void this._refresh();
  }

  private async _refresh() {
    if (!this.activeShop) return;
    const today = new Date().toISOString().slice(0, 10);
    const [orders, sales] = await Promise.all([
      ApiFactory.authenticated().listActiveOrders(this.activeShop.shopName),
      ApiFactory.authenticated().salesReport(
        this.activeShop.shopName,
        today,
        today
      ),
    ]);
    if (orders.success && orders.data) this._activeOrders = orders.data;
    if (sales.success && sales.data) this._salesReport = sales.data;
  }

  private _go(route: string) {
    router.navigate(resolveRouterPath(route));
  }

  private _money(n: number): string {
    return `${activeCurrency().symbol}${n.toLocaleString('en-PH', { maximumFractionDigits: 0 })}`;
  }

  private _renderHero() {
    const profile = this.userProfile;
    const name = profile?.displayName?.split(' ')[0] || '';
    const shop = this.activeShop;
    if (!shop) return nothing;
    const isOwner = shop.roles?.includes('owner') ?? false;
    const isLive = this.realtime?.isConnected ?? false;
    return html`
      <div class="hero">
        <div class="hero-left">
          <div class="hero-eyebrow">
            <span class="live-dot ${isLive ? '' : 'offline'}"></span>
            ${isLive ? 'Live' : 'Offline'} · today's shop
          </div>
          <h1>
            ${name ? html`Hi <span class="name-accent">${name}</span>` : 'Welcome'}
          </h1>
          <div class="hero-shop">
            <sl-icon name="shop"></sl-icon>
            <strong style="color: var(--s-ink); font-weight: 600;"
              >${shop.shopDisplayName || shop.shopName}</strong
            >
            ${isOwner ? html`<span class="role-badge">Owner</span>` : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private _renderSnapshot() {
    if (!this.activeShop) return nothing;
    const active = this._activeOrders.length;
    const activeTotal = this._activeOrders.reduce(
      (s, o) => s + (o.total ?? 0),
      0
    );
    const sales = this._salesReport?.completedCount ?? 0;
    const gross = this._salesReport?.grossSales ?? 0;
    return html`
      <div class="snapshot">
        <div class="kpi-tile accent-gold">
          <span class="icon-bubble"><sl-icon name="cart-fill"></sl-icon></span>
          <span class="kpi-label">Today · sales</span>
          <span class="kpi-value">${sales}</span>
          <span class="kpi-sub">${this._money(gross)} gross</span>
        </div>
        <div class="kpi-tile">
          <span class="icon-bubble"
            ><sl-icon name="hourglass-split"></sl-icon
          ></span>
          <span class="kpi-label">Active orders</span>
          <span class="kpi-value">${active}</span>
          <span class="kpi-sub">${this._money(activeTotal)} in flight</span>
        </div>
        <div class="kpi-tile">
          <span class="icon-bubble"><sl-icon name="graph-up"></sl-icon></span>
          <span class="kpi-label">Profit · today</span>
          <span class="kpi-value"
            >${this._money(this._salesReport?.grossProfit ?? 0)}</span
          >
          <span class="kpi-sub">vs. ${this._money(0)} yesterday</span>
        </div>
      </div>
    `;
  }

  private _renderActions() {
    const shop = this.activeShop;
    if (!shop) return nothing;
    const isOwner = shop.roles?.includes('owner') ?? false;
    const actions: QuickAction[] = [
      {
        label: 'New sale',
        description: 'Open the cashier register and ring up an order.',
        icon: 'cart-plus',
        route: 'sales/new',
        tone: 'gold',
      },
      {
        label: 'Active orders',
        description: 'Orders in progress — pay or serve.',
        icon: 'list-task',
        route: 'orders/active',
      },
      {
        label: 'Sales history',
        description: "Today's completed sales.",
        icon: 'clock-history',
        route: 'sales/history',
      },
      {
        label: 'Stock',
        description: 'Items, levels, and stocktake.',
        icon: 'box-seam',
        route: 'stock',
        ownerOnly: true,
      },
      {
        label: 'Menu',
        description: 'Products and categories.',
        icon: 'card-list',
        route: 'products',
        ownerOnly: true,
      },
      {
        label: 'Reports',
        description: 'Daily sales, profit, variance.',
        icon: 'bar-chart',
        route: 'sales/reports',
        tone: 'leaf',
        ownerOnly: true,
      },
    ];
    const filtered = actions.filter((a) => !a.ownerOnly || isOwner);
    const primary = filtered[0];
    const rest = filtered.slice(1);
    return html`
      <div>
        <div class="actions-head">
          <span class="eyebrow">Quick actions</span>
        </div>
        <div class="actions-grid">
          <button
            class="action primary"
            @click=${() => this._go(primary.route)}
          >
            <span class="action-icon"
              ><sl-icon name=${primary.icon}></sl-icon
            ></span>
            <div class="action-text">
              <div class="action-label">${primary.label}</div>
              <div class="action-desc">${primary.description}</div>
            </div>
            <span class="action-arrow"
              ><sl-icon name="arrow-right"></sl-icon
            ></span>
          </button>
          ${rest.map(
            (a) => html`
              <button
                class="action ${a.tone ?? ''}"
                @click=${() => this._go(a.route)}
              >
                <span class="action-icon"
                  ><sl-icon name=${a.icon}></sl-icon
                ></span>
                <span class="action-arrow"
                  ><sl-icon name="arrow-right"></sl-icon
                ></span>
                <div class="action-label">${a.label}</div>
                <div class="action-desc">${a.description}</div>
              </button>
            `
          )}
        </div>
      </div>
    `;
  }

  private _renderNoShop() {
    const name = this.userProfile?.displayName?.split(' ')[0] ?? '';
    return html`
      <div class="no-shop">
        <span class="eyebrow">Get started</span>
        <h2>${name ? `Welcome, ${name}.` : 'Welcome.'}</h2>
        <p>
          You haven't set up a shop yet. Create one to start taking orders,
          tracking stock, and growing your business.
        </p>
        <button class="shop-btn gold" @click=${() => this._go('shop/create')}>
          <sl-icon name="plus-circle"></sl-icon>
          Create your shop
        </button>
      </div>
    `;
  }

  render() {
    return html`
      <div class="shop-page">
        ${
          this.activeShop
            ? html`
                <div class="grid">
                  ${this._renderHero()} ${this._renderSnapshot()}
                  ${this._renderActions()}
                  <dev-tools-card></dev-tools-card>
                </div>
              `
            : this._renderNoShop()
        }
      </div>
    `;
  }
}
